import { useMemo, useState } from "react";
import { ArrowLeft, ChevronRight, Search } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { HERO_BACKGROUND, Segmented } from "@/components/panels";
import { slug } from "@/components/agent/AgentParts";
import { customerSkillTitle, customerSkillDescription, customerSkillText } from "@/components/agent/skillPresentation";
import { groupActiveSkills } from "@/components/agent/agentFacts";
import { BRAND_SPECIFIC_SKILLS, GROWTHOS_SKILLS, SKILL_CATEGORY_DESCRIPTIONS } from "@/components/agent/skillTaxonomy";
import { actionTone, formatActionLabel, formatDriftLabel, formatExactDate, formatLastUsed, formatPromotionLabel, formatUseCount, parseTimestamp } from "@/components/agent/skillUpdates";

// The group a person most wants to see first: what was made for their brand, then what GrowthOS adds.
const FIRST_GROUP = [BRAND_SPECIFIC_SKILLS, GROWTHOS_SKILLS];

function formatVersion(value) {
  if (value === null || value === undefined || value === "") return null;
  const version = String(value);
  return version.toLowerCase().startsWith("v") ? version : `v${version}`;
}

const TAGS = {
  action: "border-sm-accent/25 bg-sm-accent/[0.07] text-sm-primary",
  good: "border-[#22C55E]/25 bg-[#22C55E]/[0.08] text-[#16A34A]",
  warn: "border-[#F59E0B]/30 bg-[#F59E0B]/[0.09] text-[#B45309]",
  neutral: "border-sm-ink/10 bg-sm-ink/[0.04] text-sm-ink/60",
};
// An action word's tag and its colour in a sentence: good news is green.
const ACTION_TAG = { good: "good", warn: "warn", neutral: "neutral" };
const ACTION_INK = { good: "text-[#16A34A]", warn: "text-[#B45309]", neutral: "text-sm-ink" };

function Tag({ tone, children }) {
  return <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold ${TAGS[tone]}`}>{children}</span>;
}

/** One skill in the list: its name, what it does in two lines, and whether it changed lately. Press it to open it. */
function SkillCard({ skill, onOpen }) {
  const version = formatVersion(skill.version);
  const changed = Boolean(skill.whatChanged || skill.whyChanged);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`agent-skill-chip-${skill.id ?? skill.name}`}
      className="group flex min-w-0 flex-col rounded-xl border border-sm-ink/10 bg-white p-4 text-left transition hover:border-sm-accent/45 hover:shadow-[0_14px_30px_-20px_rgba(0,59,141,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent"
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="min-w-0 text-sm font-bold leading-snug text-sm-ink">{customerSkillTitle(skill)}</h3>
        {version ? <span className="shrink-0 rounded-md bg-sm-ink/5 px-1.5 py-0.5 font-mono text-[10px] text-sm-ink/65">{version}</span> : null}
      </div>
      <p className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-sm-ink/70">
        {customerSkillDescription(skill) || "What this skill does has not been collected from your agent yet."}
      </p>
      <div className="mt-auto flex items-center justify-between gap-3 pt-3">
        {changed ? <Tag tone={ACTION_TAG[actionTone(skill.lastAction)]}>{formatActionLabel(skill.lastAction)}{skill.changedAt ? ` · ${formatExactDate(skill.changedAt)}` : ""}</Tag> : <span className="text-[11px] text-sm-ink/50">No recent changes</span>}
        <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-sm-primary">
          Details
          <ChevronRight size={13} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </span>
      </div>
    </button>
  );
}

const CHANGES_SHOWN = 3;

/**
 * Every recorded change to one skill, newest first: the change events that name it, and the registry's own last
 * change when the events do not reach back that far (or at all).
 */
export function skillHistory(skill, changes) {
  const events = (changes ?? [])
    .filter((change) => change?.skillName === skill.name && parseTimestamp(change?.occurredAt))
    .sort((left, right) => parseTimestamp(right.occurredAt) - parseTimestamp(left.occurredAt))
    .map((change) => ({ key: change.id ?? `${change.skillName}-${change.occurredAt}`, action: change.action, at: change.occurredAt, what: change.whatChanged || null, why: change.whyChanged || null }));
  const own = skill.whatChanged || skill.whyChanged
    ? { key: "registry-last", action: skill.lastAction, at: skill.changedAt || null, what: skill.whatChanged || null, why: skill.whyChanged || null }
    : null;
  if (own && !events.some((event) => event.what === own.what && event.why === own.why)) events.push(own);
  return events;
}

function ChangeEntry({ entry }) {
  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <p className="text-[13px] font-bold text-sm-ink">
        <span className={ACTION_INK[actionTone(entry.action)]}>{formatActionLabel(entry.action)}</span>
        {entry.at ? <span className="font-normal text-sm-ink/60">{` · ${formatExactDate(entry.at)}`}</span> : null}
      </p>
      {entry.what ? <p className="mt-1 text-[13px] leading-relaxed text-sm-ink/80">{customerSkillText(entry.what)}</p> : null}
      {entry.why ? <p className="mt-1 text-[13px] leading-relaxed text-sm-ink/65"><span className="font-semibold text-sm-ink/80">Why: </span>{customerSkillText(entry.why)}</p> : null}
      {!entry.what && !entry.why ? <p className="mt-1 text-[13px] text-sm-ink/55">No summary was reported for this change.</p> : null}
    </li>
  );
}

/** One skill in full: what it does, where it comes from, and every change over time. */
function SkillDetail({ skill, category, changes, onBack }) {
  const [allChanges, setAllChanges] = useState(false);
  const version = formatVersion(skill.version);
  const promotion = formatPromotionLabel(skill.promotionStatus);
  const drift = formatDriftLabel(skill.driftStatus);
  const usage = [formatLastUsed(skill.lastUsedAt), formatUseCount(skill.useCount)].filter(Boolean).join(" · ");
  const history = skillHistory(skill, changes);
  const shownHistory = allChanges ? history : history.slice(0, CHANGES_SHOWN);
  return (
    <div data-testid="agent-skill-detail">
      <button type="button" onClick={onBack} data-testid="agent-skill-detail-back" className="inline-flex min-h-[32px] items-center gap-1.5 rounded-lg text-xs font-bold text-sm-primary hover:text-sm-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent">
        <ArrowLeft size={14} aria-hidden="true" />
        {`All ${category} skills`}
      </button>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <h3 className="font-display text-[24px] font-bold leading-tight tracking-[-0.015em] text-sm-ink">{customerSkillTitle(skill)}</h3>
        {version ? <span className="rounded-md bg-sm-ink/5 px-1.5 py-0.5 font-mono text-[11px] text-sm-ink/65">{version}</span> : null}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <Tag tone="action">{category}</Tag>
        {promotion ? <Tag tone="good">{promotion}</Tag> : null}
        {drift ? <Tag tone="warn">{drift}</Tag> : null}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] sm:items-start">
        <section className="rounded-xl border border-sm-ink/10 bg-white p-5">
          <h4 className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-sm-muted">What it does</h4>
          <p className="mt-2 text-[14px] leading-relaxed text-sm-ink/85" data-testid="agent-skill-detail-description">
            {customerSkillDescription(skill) || "What this skill does has not been collected from your agent yet. It will appear here as soon as it is."}
          </p>
          {usage ? (
            <>
              <h4 className="mt-5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-sm-muted">Usage</h4>
              <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/70" data-testid="agent-skill-detail-usage">{usage}</p>
            </>
          ) : null}
          <h4 className="mt-5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-sm-muted">Where it comes from</h4>
          <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/70">{SKILL_CATEGORY_DESCRIPTIONS[category]}</p>
        </section>
        <section className="rounded-xl border border-sm-ink/10 bg-white p-5" data-testid="agent-skill-detail-history" data-count={history.length}>
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-sm-muted">Changes over time</h4>
            {history.length ? <span className="text-xs tabular-nums text-sm-ink/55">{`${history.length} ${history.length === 1 ? "change" : "changes"}`}</span> : null}
          </div>
          {history.length ? (
            <>
              <ul className="mt-2 divide-y divide-sm-ink/[0.07]">
                {shownHistory.map((entry) => <ChangeEntry key={entry.key} entry={entry} />)}
              </ul>
              {history.length > CHANGES_SHOWN ? (
                <button type="button" data-testid="agent-skill-detail-history-toggle" onClick={() => setAllChanges((value) => !value)} className="mt-3 min-h-[28px] rounded-lg text-xs font-bold text-sm-primary hover:text-sm-ink">
                  {allChanges ? "Show recent" : `Show all ${history.length} changes`}
                </button>
              ) : null}
            </>
          ) : (
            <p className="mt-2 text-[13px] leading-relaxed text-sm-ink/60">No change has been recorded for this skill since it was installed.</p>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * The skills popup: every active skill, one group at a time, with what changed and why on each one.
 * `group` is the group to open on (null picks the one made for the brand, when it has any).
 */
export default function SkillsDialog({ open, group, onGroup, onClose, agentName, skills, reportedSkills, reportedChanges }) {
  const [search, setSearch] = useState("");
  // The skill opened from its card, or null for the list.
  const [opened, setOpened] = useState(null);
  const groups = useMemo(() => groupActiveSkills(reportedSkills, search), [reportedSkills, search]);
  const counts = Object.fromEntries((skills?.split ?? []).map((part) => [part.key, part.count]));
  const firstGroup = FIRST_GROUP.find((category) => counts[category] > 0) ?? groups[0]?.category;
  const current = groups.find((entry) => entry.category === (group ?? firstGroup)) ?? groups[0];
  const searching = search.trim().length > 0;
  const shownCount = groups.reduce((total, entry) => total + entry.skills.length, 0);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) { setSearch(""); setOpened(null); onClose(); } }}>
      <DialogContent
        data-testid="agent-skills-dialog"
        className="flex max-h-[88vh] w-[calc(100vw-24px)] max-w-[880px] flex-col gap-0 overflow-hidden rounded-[20px] border-0 bg-sm-surface p-0 text-sm-ink sm:rounded-[20px] [&>button]:right-5 [&>button]:top-5 [&>button]:text-white [&>button]:opacity-80 [&>button]:hover:opacity-100"
      >
        <div className="shrink-0 px-6 pb-6 pt-6 text-white md:px-7" style={{ background: HERO_BACKGROUND }}>
          <div className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/65">Skills</div>
          <DialogTitle className="mt-1.5 font-display text-[28px] font-extrabold leading-[1.1] tracking-[-0.02em] text-white">{`What ${agentName} can do`}</DialogTitle>
          <DialogDescription className="mt-2 text-[13px] text-white/75">
            {skills ? `${skills.active} active ${skills.active === 1 ? "skill" : "skills"} · ${skills.brandSpecific} made for your brand · ${skills.updatedThisWeek} updated in the last 7 days` : "Your agent's active skills"}
          </DialogDescription>
        </div>

        <div className="shrink-0 border-b border-sm-ink/10 bg-white px-6 py-3.5 md:px-7">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <Segmented
                testId="agent-skill-tabs"
                label="Skill group"
                value={current.category}
                onChange={(next) => { setOpened(null); onGroup(next); }}
                options={groups.map((entry) => ({
                  value: entry.category,
                  testId: `agent-skill-tab-${slug(entry.category)}`,
                  label: <>{entry.category}<span className="ml-1.5 font-medium tabular-nums text-sm-ink/45">{entry.skills.length}</span></>,
                }))}
              />
            </div>
            <label className="relative block sm:w-56">
              <span className="sr-only">Search skills</span>
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm-ink/40" aria-hidden="true" />
              <input
                id="agent-skill-search"
                data-testid="agent-skill-search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search skills"
                autoComplete="off"
                className="h-10 w-full rounded-[10px] border border-sm-ink/15 bg-white pl-9 pr-3 text-[13px] text-sm-ink outline-none placeholder:text-sm-ink/45 focus:border-sm-accent/60"
              />
            </label>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 md:px-7">
          {opened ? <SkillDetail skill={opened} category={current.category} changes={reportedChanges} onBack={() => setOpened(null)} /> : (
          <>
          <p data-testid="agent-skill-count" className="text-xs leading-relaxed text-sm-ink/65">
            {searching ? `Showing ${shownCount} of ${skills?.active ?? shownCount} active skills. ` : ""}
            {SKILL_CATEGORY_DESCRIPTIONS[current.category]}
          </p>
          {current.skills.length === 0 ? (
            <p className="mt-4 rounded-xl border border-dashed border-sm-ink/15 bg-white px-4 py-8 text-center text-[13px] text-sm-ink/60">
              {searching ? "No skill in this group matches that search." : "No active skills in this group."}
            </p>
          ) : (
            <div data-testid={`agent-skill-group-list-${slug(current.category)}`} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {current.skills.map((skill) => <SkillCard key={`${current.category}-${skill.name}`} skill={skill} onOpen={() => setOpened(skill)} />)}
            </div>
          )}
          </>
          )}
          <p className="mt-5 text-[11px] leading-relaxed text-sm-ink/55">
            Skill details come straight from your agent's skill registry. Nothing here has been summarized or filled in by the dashboard, so a missing field means the registry did not report one.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
